import styled from 'styled-components';

/* Design bands are max-width, not the theme's min-width set. */
const abp = (key: string) => (props: any) => props.theme.awarenessBreakpoints && props.theme.awarenessBreakpoints[key];

const WRAP = '1240px';
const CONTENT_WIDTH = '860px';
const BLACK = '#161719';
const TEXT_MUTED = '#5F6068';
const BAND = '#F0F0F6';
const FOCUS_BLUE = '#3A70D9';

/* ---------------------------------------------------------------- Hero ---- */

const HeroSection = styled.section`
    position: relative;
    width: 100%;
    aspect-ratio: 1440 / 323;
    overflow: hidden;
    background: ${BLACK};

    @media screen and (max-width: ${abp('w820')}) {
        aspect-ratio: auto;
        height: 280px;
    }

    @media screen and (max-width: ${abp('w600')}) {
        height: 220px;
    }
`;

const HeroMedia = styled.div<{ isMobileOnly?: boolean }>`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;

    display: ${props => (props.isMobileOnly ? 'none' : 'block')};

    @media screen and (max-width: ${abp('w600')}) {
        display: ${props => (props.isMobileOnly ? 'block' : 'none')};
    }

    picture, img {
        width: 100%;
        height: 100%;
    }

    /* The design mirrors its own stock photo and crops low; the CMS image is a
       different picture, so it keeps a crop that holds the faces. */
    img {
        object-fit: cover;
        object-position: center 25%;
    }
`;

const HeroInner = styled.div`
    position: relative;
    height: 100%;
    max-width: ${WRAP};
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 16px;
`;

const HeroTitle = styled.h1`
    max-width: 400px;
    margin: 0;
    font-family: Anton;
    font-weight: 400;
    font-size: 48px;
    line-height: 60px;
    letter-spacing: 0.01em;
    text-transform: uppercase;
    color: #fff;

    @media screen and (max-width: ${abp('w820')}) {
        font-size: 40px;
        line-height: 46px;
    }

    @media screen and (max-width: ${abp('w600')}) {
        font-size: 32px;
        line-height: 38px;
        max-width: 260px;
    }
`;

/* The shared Button renders its own anchor; this restyles it to the design's
   pill rather than adding another button variant. */
const HeroCtaWrap = styled.div`
    && a {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: auto;
        min-height: 0;
        background: ${BLACK};
        color: #fff;
        font-family: Overpass;
        font-weight: 700;
        font-size: 16px;
        line-height: 20px;
        padding: 12px 32px;
        border: 0;
        border-radius: 24px;
        text-decoration: none;

        /* Button wraps the label in Text, which is 20px / 28px on desktop. */
        span {
            font-size: 16px;
            line-height: 20px;
            font-weight: 700;
        }
    }

    && a:hover {
        background: #000;
    }
`;

/* ---------------------------------------------------------------- Body ---- */

const BodySection = styled.section`
    background: #fff;
    padding: 56px 0;

    @media screen and (max-width: ${abp('w600')}) {
        padding: 40px 0;
    }
`;

const BodyWrap = styled.div`
    max-width: ${WRAP};
    margin: 0 auto;
    padding: 0 20px;
`;

const BodyInner = styled.div`
    max-width: ${CONTENT_WIDTH};

    > *:last-child {
        margin-bottom: 0;
    }
`;

const Intro = styled.div`
    p {
        margin: 0 0 12px;
        font-family: Lora;
        font-weight: 400;
        font-size: 24px;
        line-height: 32px;
        color: #000;
    }

    p + p {
        margin-bottom: 32px;
    }

    @media screen and (max-width: ${abp('w600')}) {
        p {
            font-size: 20px;
            line-height: 28px;
        }
    }
`;

/* Headings, paragraphs and lists all come from one rich text field, so the
   styling is by tag rather than by component. The design has no rule for a
   heading inside the article, so the heading block is ours. */
const Body = styled.div`
    color: #000;

    h1, h2, h3, h4, h5 {
        margin: 36px 0 4px;
        font-family: Overpass;
        font-weight: 700;
        text-transform: uppercase;
        color: ${BLACK};
    }

    h1, h2 {
        font-size: 24px;
        line-height: 36px;
    }

    h3, h4, h5 {
        font-size: 18px;
        line-height: 26px;
    }

    p {
        margin: 0 0 24px;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 28px;
    }

    ul, ol {
        margin: 0 0 24px;
        padding-left: 22px;
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    li {
        margin: 0;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 28px;
    }

    @media screen and (max-width: ${abp('w600')}) {
        p,
        li {
            font-size: 18px;
            line-height: 26px;
        }
    }

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

/* -------------------------------------------------------------- Sign up ---- */

const SignupSection = styled.section`
    background: ${BAND};
    padding: 48px 0;
`;

const SignupInner = styled.div`
    max-width: ${WRAP};
    margin: 0 auto;
    padding: 0 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 36px;
    row-gap: 48px;
    align-items: start;

    @media screen and (max-width: ${abp('w820')}) {
        grid-template-columns: 1fr;
        gap: 28px;
    }
`;

const SignupIntro = styled.div`
    font-family: Overpass;
    font-weight: 300;
    color: #000;
    font-size: 20px;
    line-height: 28px;

    p {
        margin: 0 0 16px;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        line-height: 28px;
        color: #000;
    }

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

const ReadMoreLink = styled.div`
    margin: 8px 0 0;

    /* The shared Button renders its own anchor. Strip the pill styling so the
       arrow variant reads as the inline link the design asks for, the same way
       the way cards' CTA does. */
    a {
        padding-left: 0;
        padding-right: 0;
        border: 0;
        background: none;
        font-family: Overpass;
        font-weight: 300;
        font-size: 20px;
        color: #000;
    }
`;

/* ---------------------------------------------------------------- Ways ---- */

const WaysSection = styled.section`
    background: #fff;
    padding: 72px 0 48px;

    @media screen and (max-width: ${abp('w600')}) {
        padding: 56px 0 40px;
    }
`;

const WaysInner = styled.div`
    max-width: ${WRAP};
    margin: 0 auto;
    padding: 0 20px;
`;

const WaysEyebrow = styled.p`
    margin: 0 0 12px;
    font-family: Overpass;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${TEXT_MUTED};
    text-align: center;
`;

const WaysHeading = styled.h2`
    margin: 0 auto 40px;
    max-width: 640px;
    font-family: Anton;
    font-weight: 400;
    font-size: clamp(30px, 4vw, 48px);
    line-height: 1.06;
    text-transform: uppercase;
    color: ${BLACK};
    text-align: center;
`;

const WaysGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 40px;
    row-gap: 44px;

    @media screen and (max-width: ${abp('w900')}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${abp('w600')}) {
        grid-template-columns: 1fr;
    }
`;

const WayCardImage = styled.div`
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 368 / 173;
    overflow: hidden;

    picture, img {
        display: block;
        width: 100%;
        height: 100%;
    }

    img {
        object-fit: cover;
        object-position: center;
        filter: grayscale(1);
    }
`;

const WayCardTitle = styled.h3`
    margin: 0;
    font-family: Overpass;
    font-weight: 600;
    font-size: 24px;
    line-height: 28px;
    text-transform: uppercase;
    color: ${BLACK};
`;

const WayCardCta = styled.span`
    display: block;
    font-family: Overpass;
    font-weight: 300;
    font-size: 20px;
    line-height: 28px;
    color: ${BLACK};

    > svg {
        display: inline-block;
        vertical-align: middle;
        margin-left: 2px;
        width: 16px;
        height: 16px;
    }
`;

const WayCard = styled.a`
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-decoration: none;

    &:focus-visible {
        outline: 3px solid ${FOCUS_BLUE};
        outline-offset: 3px;
    }
`;

/* ---------------------------------------------------------- Back to top ---- */

const BackToTop = styled.div`
    background: #fff;
    display: flex;
    justify-content: center;
    padding: 28px 0;
`;

const BackToTopLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: Overpass;
    font-weight: 400;
    font-size: 16px;
    color: #000;
    text-decoration: none;

    &:hover,
    &:focus {
        text-decoration: underline;
        text-underline-offset: 3px;
    }

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
    }
`;

export {
    HeroSection,
    HeroMedia,
    HeroInner,
    HeroTitle,
    HeroCtaWrap,
    BodySection,
    BodyWrap,
    BodyInner,
    Intro,
    Body,
    SignupSection,
    SignupInner,
    SignupIntro,
    ReadMoreLink,
    WaysSection,
    WaysInner,
    WaysEyebrow,
    WaysHeading,
    WaysGrid,
    WayCard,
    WayCardImage,
    WayCardTitle,
    WayCardCta,
    BackToTop,
    BackToTopLink,
};
